import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import MarkFeeModal from './MarkFeeModal';
import FeeReceiptGenerator from './FeeReceiptGenerator';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface FeeRecord {
  student_id: number | string;
  student_name: string;
  class: string;
  section: string | null;
  roll_no: number;
  fee_record_id: number | null;
  session: string;
  month_year: string;
  receipt_no: string | null;
  paid_date: string | null;
  total_amount: string | number | null;
  paid_amount: string | number | null;
  fees_status: 'paid' | 'unpaid' | 'partial';
  pay_mode: string | null;
  profile_picture_url?: string | null;
}

interface MonthStatus {
  monthYear: string;
  displayName: string;
  status: 'paid' | 'unpaid' | 'partial' | 'upcoming';
  record?: FeeRecord;
}

interface StudentFeePanelProps {
  studentId: number | string | null;
  session: string;
  sessionStart: string;
  sessionEnd: string;
  studentRecord?: {
    student_name: string;
    profile_picture_url?: string | null;
    class: string;
    section: string | null;
    roll_no: number;
  } | null;
  onClose: () => void;
  onPaymentSuccess?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const fmtAmount = (v: string | number | null) => {
  if (v === null || v === undefined) return '₹0';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN')}`;
};

const fmtDate = (d: string | null) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtMonthFull = (my: string) => {
  const [year, month] = my.split('-');
  return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
};

const monthYearToMonthName = (my: string): string => {
  const [, month] = my.split('-');
  return MONTH_NAMES[parseInt(month) - 1] ?? '';
};

const getCurrentMonthYear = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const generateSessionMonths = (
  session: string,
  startMonth: string,
  endMonth: string,
): MonthStatus[] => {
  const [yearAStr, yearBStr] = session.split('-');
  const yearA = parseInt(yearAStr);
  const yearB = parseInt(yearBStr);

  const startIdx = MONTH_NAMES.findIndex(m => m.toLowerCase() === startMonth.toLowerCase());
  const endIdx   = MONTH_NAMES.findIndex(m => m.toLowerCase() === endMonth.toLowerCase());

  if (startIdx === -1 || endIdx === -1) {
    return generateFallbackMonths(session);
  }

  const months: MonthStatus[] = [];
  let i = startIdx;
  let wrapped = false;

  while (true) {
    const year = wrapped ? yearB : yearA;
    const mm   = String(i + 1).padStart(2, '0');
    const monthYear = `${year}-${mm}`;
    months.push({
      monthYear,
      displayName: `${MONTH_NAMES[i]} ${year}`,
      status: 'upcoming',
    });
    if (i === endIdx) break;
    i = (i + 1) % 12;
    if (i === 0) wrapped = true;
  }

  return months;
};

const generateFallbackMonths = (session: string): MonthStatus[] => {
  const [startYear, endYear] = session.split('-').map(Number);
  const order = ['November','December','January','February','March','April','May','June','July','August','September','October'];
  const months: MonthStatus[] = [];
  let year = startYear;
  let monthIdx = 10;
  for (let i = 0; i < 12; i++) {
    const mm = String(monthIdx + 1).padStart(2, '0');
    months.push({ monthYear: `${year}-${mm}`, displayName: `${order[i]} ${year}`, status: 'upcoming' });
    monthIdx++;
    if (monthIdx > 11) { monthIdx = 0; year = endYear; }
  }
  return months;
};

/**
 * KEY FIX: if status is 'upcoming', always return 'upcoming' regardless of record.
 * The API returns 'unpaid' for future months which is wrong — the timeline
 * already correctly marks them as upcoming, so we just respect that.
 */
const getEffectiveStatus = (status: string, record?: FeeRecord): 'paid' | 'unpaid' | 'partial' | 'upcoming' => {
  // upcoming always wins — don't let API record status override it
  if (status === 'upcoming') return 'upcoming';
  if (!record) return status as 'paid' | 'unpaid' | 'partial';
  const paid  = parseFloat(String(record.paid_amount  ?? 0));
  const total = parseFloat(String(record.total_amount ?? 0));
  if (total > 0 && paid >= total) return 'paid';
  if (paid > 0 && paid < total)   return 'partial';
  if (paid === 0)                  return 'unpaid';
  return status as 'paid' | 'unpaid' | 'partial';
};

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  paid:     { bg: 'rgba(16,185,129,0.1)',  text: '#059669', border: 'rgba(16,185,129,0.25)', dot: '#10b981' },
  unpaid:   { bg: 'rgba(239,68,68,0.1)',   text: '#dc2626', border: 'rgba(239,68,68,0.25)',  dot: '#ef4444' },
  partial:  { bg: 'rgba(245,158,11,0.1)',  text: '#d97706', border: 'rgba(245,158,11,0.25)', dot: '#f59e0b' },
  upcoming: { bg: 'rgba(59,130,246,0.08)', text: '#2563eb', border: 'rgba(59,130,246,0.2)',  dot: '#3b82f6' },
};

const STATUS_ICONS: Record<string, string> = {
  paid:     'pi-check-circle',
  unpaid:   'pi-times-circle',
  partial:  'pi-exclamation-circle',
  upcoming: 'pi-clock',
};

// ─────────────────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────────────────
const PANEL_CSS = `
  @keyframes sfp-slideIn  { from{opacity:0;transform:translateX(24px)} to{opacity:1;transform:translateX(0)} }
  @keyframes sfp-fadeIn   { from{opacity:0} to{opacity:1} }
  @keyframes sfp-slideUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  @keyframes sfp-shimmer  { 0%{background-position:-500px 0} 100%{background-position:500px 0} }

  .sfp-panel {
    position: fixed; top: 0; right: 0; bottom: 0;
    width: 420px;
    background: #fff;
    box-shadow: -8px 0 48px rgba(30,58,138,0.13);
    display: flex; flex-direction: column;
    z-index: 1000;
    animation: sfp-slideIn 0.28s cubic-bezier(0.4,0,0.2,1);
    font-family: 'DM Sans', system-ui, sans-serif;
  }

  .sfp-scroll::-webkit-scrollbar { width: 4px; }
  .sfp-scroll::-webkit-scrollbar-track { background: transparent; }
  .sfp-scroll::-webkit-scrollbar-thumb { background: rgba(30,58,138,0.12); border-radius: 4px; }

  .sfp-close-btn {
    width: 32px; height: 32px; border-radius: 9px;
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.25);
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    padding: 0; transition: background 0.15s; flex-shrink: 0;
  }
  .sfp-close-btn:hover { background: rgba(255,255,255,0.28); }

  .sfp-month-row {
    border-radius: 12px;
    border: 1px solid #f0f4f8;
    overflow: hidden;
    background: white;
    margin-bottom: 8px;
    animation: sfp-slideUp 0.2s ease both;
    transition: box-shadow 0.15s, transform 0.13s;
  }
  .sfp-month-row:hover {
    box-shadow: 0 3px 16px rgba(30,58,138,0.09);
    transform: translateY(-1px);
  }

  .sfp-shimmer {
    background: linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%);
    background-size: 500px 100%;
    animation: sfp-shimmer 1.3s infinite linear;
    border-radius: 6px;
  }

  .sfp-mark-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 10px; border: none;
    background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
    color: white; cursor: pointer; font-size: 12px; font-weight: 700;
    font-family: inherit;
    box-shadow: 0 4px 12px rgba(30,58,138,0.25);
    transition: box-shadow 0.15s, transform 0.13s;
  }
  .sfp-mark-btn:hover {
    box-shadow: 0 6px 18px rgba(30,58,138,0.35);
    transform: translateY(-1px);
  }
  .sfp-mark-btn:disabled {
    opacity: 0.5; cursor: not-allowed; transform: none;
  }

  .sfp-receipt-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 10px;
    border: 1px solid rgba(192,81,10,0.35);
    background: rgba(192,81,10,0.06);
    color: #C0510A; cursor: pointer; font-size: 12px; font-weight: 700;
    font-family: inherit;
    transition: background 0.15s, transform 0.13s;
  }
  .sfp-receipt-btn:hover {
    background: rgba(192,81,10,0.12);
    transform: translateY(-1px);
  }

  .sfp-receipt-all-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: 10px;
    border: 1px solid rgba(192,81,10,0.3);
    background: transparent;
    color: #C0510A; cursor: pointer; font-size: 11px; font-weight: 700;
    font-family: inherit;
    transition: background 0.15s;
  }
  .sfp-receipt-all-btn:hover { background: rgba(192,81,10,0.07); }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Avatar
// ─────────────────────────────────────────────────────────────────────────────
const PanelAvatar: React.FC<{ name: string; pictureUrl?: string | null; size?: number }> = ({
  name, pictureUrl, size = 52,
}) => {
  const [err, setErr] = useState(false);
  if (pictureUrl && !err) {
    return (
      <img
        src={pictureUrl} alt={name} onError={() => setErr(true)}
        style={{
          width: size, height: size, borderRadius: '50%',
          objectFit: 'cover', flexShrink: 0,
          border: '3px solid rgba(255,255,255,0.4)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'rgba(255,255,255,0.2)',
      border: '3px solid rgba(255,255,255,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700, color: 'white',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = STATUS_COLORS[status] ?? STATUS_COLORS.upcoming;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 20,
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>
      <i className={`pi ${STATUS_ICONS[status] ?? 'pi-circle'}`} style={{ fontSize: 10 }} />
      {status}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Month card
// ─────────────────────────────────────────────────────────────────────────────
const MonthCard: React.FC<{
  month: MonthStatus;
  index: number;
  isCurrentMonth: boolean;
  studentId: number | string;
  session: string;
  onMarkPayment: (monthYear: string) => void;
  onPrintReceipt: (monthYear: string) => void;
}> = ({ month, index, isCurrentMonth, onMarkPayment, onPrintReceipt }) => {
  const [open, setOpen] = useState(isCurrentMonth);
  const effectiveStatus = getEffectiveStatus(month.status, month.record);
  const sc = STATUS_COLORS[effectiveStatus];

  // upcoming months CAN be marked paid (advance payment)
  const canMarkPayment  = effectiveStatus === 'unpaid' || effectiveStatus === 'partial' || effectiveStatus === 'upcoming';
  const canPrintReceipt = effectiveStatus === 'paid'   || effectiveStatus === 'partial';

  return (
    <div
      className="sfp-month-row"
      style={{
        animationDelay: `${index * 0.03}s`,
        border: isCurrentMonth ? `1.5px solid ${sc.border}` : undefined,
      }}
    >
      {/* Accent bar */}
      <div style={{ height: 3, background: `linear-gradient(90deg, ${sc.dot}, ${sc.dot}88)` }} />

      {/* Header row */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '12px 16px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', cursor: 'pointer',
          background: isCurrentMonth ? sc.bg : 'white',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: sc.bg, border: `1px solid ${sc.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className={`pi ${STATUS_ICONS[effectiveStatus]}`} style={{ fontSize: 15, color: sc.text }} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>
              {fmtMonthFull(month.monthYear)}
              {isCurrentMonth && (
                <span style={{
                  marginLeft: 7, fontSize: 9, fontWeight: 800,
                  background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
                  color: 'white', padding: '2px 7px', borderRadius: 10,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>Current</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              {month.record?.receipt_no
                ? `Receipt: ${month.record.receipt_no}`
                : effectiveStatus === 'upcoming'
                  ? 'Upcoming month'
                  : effectiveStatus === 'unpaid'
                    ? 'No payment recorded'
                    : 'Partial payment'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StatusBadge status={effectiveStatus} />
          <i
            className={`pi ${open ? 'pi-chevron-up' : 'pi-chevron-down'}`}
            style={{ fontSize: 11, color: '#9ca3af' }}
          />
        </div>
      </div>

      {/* Expanded body */}
      {open && (
        <div style={{
          padding: '0 16px 16px',
          borderTop: '1px solid #f3f4f6',
          animation: 'sfp-fadeIn 0.18s ease',
        }}>
          {month.record && effectiveStatus !== 'upcoming' ? (
            <div style={{ paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Amounts */}
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  { label: 'Total Amount', val: fmtAmount(month.record.total_amount), color: '#1f2937' },
                  { label: 'Paid Amount',  val: fmtAmount(month.record.paid_amount),  color: effectiveStatus === 'paid' ? '#059669' : '#dc2626' },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{
                    flex: 1, background: '#f8fafc', borderRadius: 10,
                    padding: '10px 12px', textAlign: 'center', border: '1px solid #f0f4f8',
                  }}>
                    <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Meta details */}
              {([
                month.record.paid_date  && { label: 'Paid On',      val: fmtDate(month.record.paid_date)!,    icon: 'pi-calendar-check' },
                month.record.pay_mode   && { label: 'Payment Mode', val: month.record.pay_mode.toUpperCase(), icon: 'pi-credit-card'    },
                month.record.receipt_no && { label: 'Receipt No',   val: month.record.receipt_no,             icon: 'pi-file'           },
              ] as ({ label: string; val: string; icon: string } | false)[]).filter(Boolean).map((item) => {
                const i = item as { label: string; val: string; icon: string };
                return (
                  <div key={i.label} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderRadius: 8,
                    background: '#f8fafc', border: '1px solid #f0f4f8',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <i className={`pi ${i.icon}`} style={{ fontSize: 13, color: '#9ca3af' }} />
                      <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{i.label}</span>
                    </div>
                    <span style={{ fontSize: 12, color: '#1f2937', fontWeight: 700 }}>{i.val}</span>
                  </div>
                );
              })}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                {canMarkPayment && (
                  <button
                    className="sfp-mark-btn"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={(e) => { e.stopPropagation(); onMarkPayment(month.monthYear); }}
                  >
                    <i className="pi pi-indian-rupee" style={{ fontSize: 12 }} />
                    Mark as Paid
                  </button>
                )}
                {canPrintReceipt && (
                  <button
                    className="sfp-receipt-btn"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={(e) => { e.stopPropagation(); onPrintReceipt(month.monthYear); }}
                  >
                    <i className="pi pi-file-pdf" style={{ fontSize: 12 }} />
                    Print Receipt
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div style={{ paddingTop: 14 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', borderRadius: 10, marginBottom: canMarkPayment ? 12 : 0,
                background: effectiveStatus === 'upcoming' ? 'rgba(59,130,246,0.04)' : 'rgba(239,68,68,0.04)',
                border: `1px solid ${effectiveStatus === 'upcoming' ? 'rgba(59,130,246,0.15)' : 'rgba(239,68,68,0.12)'}`,
              }}>
                <i
                  className={`pi ${effectiveStatus === 'upcoming' ? 'pi-clock' : 'pi-info-circle'}`}
                  style={{ fontSize: 17, color: effectiveStatus === 'upcoming' ? '#3b82f6' : '#ef4444', flexShrink: 0 }}
                />
                <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, lineHeight: 1.5 }}>
                  {effectiveStatus === 'upcoming'
                    ? 'This month is upcoming — you can still record an advance payment.'
                    : 'No payment has been recorded for this month.'}
                </span>
              </div>

              {canMarkPayment && (
                <button
                  className="sfp-mark-btn"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={(e) => { e.stopPropagation(); onMarkPayment(month.monthYear); }}
                >
                  <i className="pi pi-indian-rupee" style={{ fontSize: 12 }} />
                  {effectiveStatus === 'upcoming' ? 'Record Advance Payment' : 'Mark as Paid'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────
const StudentFeePanel: React.FC<StudentFeePanelProps> = ({
  studentId, session, sessionStart, sessionEnd,
  studentRecord, onClose, onPaymentSuccess,
}) => {
  const toast = useRef<Toast>(null);
  const [records,  setRecords]  = useState<FeeRecord[]>([]);
  const [loading,  setLoading]  = useState(true);

  const [markModalOpen, setMarkModalOpen] = useState(false);
  const [markMonthYear, setMarkMonthYear] = useState<string | null>(null);

  const [receiptOpen,   setReceiptOpen]   = useState(false);
  const [receiptMonths, setReceiptMonths] = useState<string[]>([]);

  const apiDateRange = (() => {
    const [yearAStr, yearBStr] = session.split('-');
    const yearA = parseInt(yearAStr);
    const yearB = parseInt(yearBStr);

    const startIdx = MONTH_NAMES.findIndex(m => m.toLowerCase() === sessionStart.toLowerCase());
    const endIdx   = MONTH_NAMES.findIndex(m => m.toLowerCase() === sessionEnd.toLowerCase());

    const startMM = String(startIdx + 1).padStart(2, '0');
    const endMM   = String(endIdx + 1).padStart(2, '0');

    return {
      startMonthYear: `${yearA}-${startMM}`,
      endMonthYear:   startIdx > endIdx ? `${yearB}-${endMM}` : `${yearA}-${endMM}`,
    };
  })();

  const fetchDetails = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(
        `students/monthly-fees-records?student_id=${studentId}&session=${session}&start_month_year=${apiDateRange.startMonthYear}&end_month_year=${apiDateRange.endMonthYear}`
      );
      if (res.data.status === 'success') setRecords(res.data.data || []);
    } catch (e) {
      console.error('Failed to load student fees', e);
    } finally {
      setLoading(false);
    }
  }, [studentId, session, apiDateRange.startMonthYear, apiDateRange.endMonthYear]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  // Build timeline
  const timeline: MonthStatus[] = (() => {
    const sessionMonths = generateSessionMonths(session, sessionStart, sessionEnd);
    const map = new Map<string, FeeRecord>();
    records.forEach(r => map.set(r.month_year, r));
    const now = new Date();

    return sessionMonths.map(m => {
      const rec = map.get(m.monthYear);
      const [y, mo] = m.monthYear.split('-').map(Number);
      // A month is "upcoming" if it hasn't started yet
      const isUpcoming = new Date(y, mo - 1) > now;

      if (isUpcoming) {
        // Keep the record (for advance payment support) but force status to upcoming
        // The API incorrectly returns 'unpaid' for future months — we ignore that
        return { ...m, status: 'upcoming' as const, record: rec };
      }
      if (rec) return { ...m, status: rec.fees_status, record: rec };
      return { ...m, status: 'unpaid' as const };
    });
  })();

  // Stats — only count past/current months
  const stats = records.reduce(
    (acc, r) => {
      const [y, mo] = r.month_year.split('-').map(Number);
      const isUpcoming = new Date(y, mo - 1) > new Date();
      if (isUpcoming) return acc; // skip future months from stats

      const paid  = parseFloat(String(r.paid_amount  ?? 0));
      const total = parseFloat(String(r.total_amount ?? 0));
      if (r.fees_status === 'paid')                acc.paid   += paid;
      if (r.fees_status === 'unpaid' && total > 0) acc.unpaid += total;
      acc.months[r.fees_status] = (acc.months[r.fees_status] || 0) + 1;
      return acc;
    },
    { paid: 0, unpaid: 0, months: {} as Record<string, number> }
  );

  const currentMonthYear = getCurrentMonthYear();
  const studentName      = studentRecord?.student_name ?? (records[0]?.student_name ?? 'Student');
  const studentClass     = studentRecord?.class        ?? records[0]?.class ?? '';
  const studentSection   = studentRecord?.section      ?? records[0]?.section ?? null;
  const studentRoll      = studentRecord?.roll_no      ?? records[0]?.roll_no ?? '';

  const handleMarkPayment = (monthYear: string) => {
    setMarkMonthYear(monthYear);
    setMarkModalOpen(true);
  };

  const handlePrintReceipt = (monthYear: string) => {
    setReceiptMonths([monthYearToMonthName(monthYear)]);
    setReceiptOpen(true);
  };

  const handlePrintAllReceipts = () => {
    const paidMonthNames = timeline
      .filter(m => {
        const es = getEffectiveStatus(m.status, m.record);
        return es === 'paid' || es === 'partial';
      })
      .map(m => monthYearToMonthName(m.monthYear))
      .filter(Boolean);
    if (paidMonthNames.length === 0) return;
    setReceiptMonths(paidMonthNames);
    setReceiptOpen(true);
  };

  const handlePaymentSuccess = () => {
    setMarkModalOpen(false);
    setMarkMonthYear(null);
    fetchDetails();
    onPaymentSuccess?.();
  };

  const paidMonthsCount = timeline.filter(m => {
    const es = getEffectiveStatus(m.status, m.record);
    return es === 'paid' || es === 'partial';
  }).length;

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <style>{PANEL_CSS}</style>

      <div className="sfp-panel">

        {/* ── Header ── */}
        <div style={{
          flexShrink: 0, padding: '20px 20px 18px',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #2855ae 55%, #3b82f6 100%)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
            <PanelAvatar name={studentName} pictureUrl={studentRecord?.profile_picture_url} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: 'white', lineHeight: 1.2, marginBottom: 6 }}>
                {studentName}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[
                  { icon: 'pi-book',     label: `Class ${studentClass}${studentSection ? ` · ${studentSection}` : ''}` },
                  { icon: 'pi-id-card',  label: `Roll ${studentRoll}` },
                  { icon: 'pi-calendar', label: session },
                ].map(({ icon, label }) => (
                  <span key={label} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.75)',
                    background: 'rgba(255,255,255,0.12)', padding: '3px 9px',
                    borderRadius: 20, border: '1px solid rgba(255,255,255,0.18)',
                  }}>
                    <i className={`pi ${icon}`} style={{ fontSize: 10 }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <button className="sfp-close-btn" onClick={onClose}>
              <i className="pi pi-times" style={{ fontSize: 12, color: 'white' }} />
            </button>
          </div>

          {/* Stat chips */}
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { label: 'Paid',        val: fmtAmount(stats.paid),   color: '#34d399', bg: 'rgba(52,211,153,0.15)',  border: 'rgba(52,211,153,0.3)'  },
              { label: 'Due',         val: fmtAmount(stats.unpaid), color: '#f87171', bg: 'rgba(248,113,113,0.15)', border: 'rgba(248,113,113,0.3)' },
              { label: 'Months Paid', val: String(stats.months['paid'] ?? 0), color: 'rgba(255,255,255,0.9)', bg: 'rgba(255,255,255,0.1)', border: 'rgba(255,255,255,0.2)' },
            ].map(({ label, val, color, bg, border }) => (
              <div key={label} style={{
                flex: 1, borderRadius: 10, padding: '9px 10px',
                background: bg, border: `1px solid ${border}`, textAlign: 'center',
              }}>
                <div style={{ fontSize: 14, fontWeight: 800, color, marginBottom: 2 }}>{val}</div>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Legend + dot timeline + bulk receipt ── */}
        <div style={{
          flexShrink: 0, padding: '10px 16px 10px',
          borderBottom: '1px solid #f0f4f8', background: '#fafbfc',
        }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
            {(['paid','unpaid','partial','upcoming'] as const).map(s => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[s].dot }} />
                <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'capitalize' }}>{s}</span>
              </div>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 3 }}>
              {timeline.map(m => {
                const es = getEffectiveStatus(m.status, m.record);
                const isCur = m.monthYear === currentMonthYear;
                return (
                  <div key={m.monthYear} title={fmtMonthFull(m.monthYear)} style={{
                    width: isCur ? 10 : 7, height: isCur ? 10 : 7,
                    borderRadius: '50%', background: STATUS_COLORS[es].dot,
                    border: isCur ? '2px solid #1e3a8a' : 'none',
                  }} />
                );
              })}
            </div>
          </div>

          {paidMonthsCount > 0 && (
            <button className="sfp-receipt-all-btn" onClick={handlePrintAllReceipts}>
              <i className="pi pi-file-pdf" style={{ fontSize: 11 }} />
              Print All Receipts ({paidMonthsCount} month{paidMonthsCount !== 1 ? 's' : ''})
            </button>
          )}
        </div>

        {/* ── Month list ── */}
        <div className="sfp-scroll" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #f0f4f8', marginBottom: 8 }}>
                <div className="sfp-shimmer" style={{ height: 3 }} />
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="sfp-shimmer" style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0 }} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div className="sfp-shimmer" style={{ height: 13, width: '45%' }} />
                    <div className="sfp-shimmer" style={{ height: 10, width: '30%' }} />
                  </div>
                  <div className="sfp-shimmer" style={{ height: 22, width: 60, borderRadius: 20 }} />
                </div>
              </div>
            ))
          ) : (
            timeline.map((month, i) => (
              <MonthCard
                key={month.monthYear}
                month={month}
                index={i}
                isCurrentMonth={month.monthYear === currentMonthYear}
                studentId={studentId!}
                session={session}
                onMarkPayment={handleMarkPayment}
                onPrintReceipt={handlePrintReceipt}
              />
            ))
          )}
        </div>
      </div>

      <MarkFeeModal
        open={markModalOpen}
        onClose={() => { setMarkModalOpen(false); setMarkMonthYear(null); }}
        studentId={studentId !== null ? String(studentId) : null}
        session={session}
        monthYear={markMonthYear}
        onSuccess={handlePaymentSuccess}
        toast={toast}
      />

      {receiptOpen && (
        <FeeReceiptGenerator
          studentName={studentName}
          studentClass={`Class ${studentClass}`}
          rollNo={studentRoll}
          session={session}
          feeRecords={records}
          preSelectedMonths={receiptMonths}
          onClose={() => setReceiptOpen(false)}
        />
      )}
    </>
  );
};

export default StudentFeePanel;